import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useFuelStations } from "../../api/fuelStations";
import { useDeleteHistoryEntries } from "../../api/mutations";
import { useFormOptions, useHistoryEntries } from "../../api/queries";
import type { DeleteHistoryItem, HistoryEntry, HistoryEntryType } from "../../api/types";
import { useActiveCar } from "../../auth/useActiveCar";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import MoreMenu, { MoreMenuItem } from "../account/MoreMenu";
import EntryDetailModal from "../overview/EntryDetailModal";
import HistoryList from "../overview/HistoryList";
import NewEntryModal from "../overview/NewEntryModal";
import OdometerEntryModal from "./OdometerEntryModal";

const HISTORY_FILTERS: { type: HistoryEntryType; label: string }[] = [
  { type: "fuel", label: "Fuel entries" },
  { type: "odometer", label: "Odometer entries" },
];

function historyEntryKey(entry: HistoryEntry | DeleteHistoryItem): string {
  return `${entry.entry_type}:${entry.id}`;
}

export default function HistoryPage() {
  const { cars, activeCar, hasCars, isLoading: activeCarLoading } = useActiveCar();
  const [visibleTypes, setVisibleTypes] = useState<Set<HistoryEntryType>>(
    () => new Set(["fuel", "odometer"]),
  );
  const historyTypes = useMemo(
    () =>
      HISTORY_FILTERS.map((item) => item.type).filter((type) =>
        visibleTypes.has(type),
      ),
    [visibleTypes],
  );
  const history = useHistoryEntries(10, historyTypes);
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = history;
  const formOptions = useFormOptions();
  const fuelStations = useFuelStations();
  const deleteEntries = useDeleteHistoryEntries();
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isOdometerModalOpen, setIsOdometerModalOpen] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<Map<string, DeleteHistoryItem>>(
    () => new Map(),
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const entries = useMemo(
    () => data?.pages.flatMap((page) => page.entries) ?? [],
    [data?.pages],
  );
  const canFetchNextPage = Boolean(hasNextPage);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !canFetchNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [canFetchNextPage, fetchNextPage, isFetchingNextPage]);

  useEffect(() => {
    setSelectedEntries(new Map());
    setDeleteConfirmOpen(false);
    setDeleteError(null);
  }, [historyTypes.join(",")]);

  const selectedCount = selectedEntries.size;
  const openFuelModal = () => setIsFuelModalOpen(true);
  const closeFuelModal = () => setIsFuelModalOpen(false);

  const toggleSelectionMode = () => {
    if (selectionMode) {
      setSelectionMode(false);
      setSelectedEntries(new Map());
      setDeleteConfirmOpen(false);
      setDeleteError(null);
      return;
    }
    setSelectionMode(true);
    setSelectedEntryId(null);
  };

  const toggleEntrySelection = (entry: HistoryEntry) => {
    setSelectedEntries((prev) => {
      const next = new Map(prev);
      const key = historyEntryKey(entry);
      if (next.has(key)) next.delete(key);
      else next.set(key, { entry_type: entry.entry_type, id: entry.id });
      return next;
    });
  };

  const toggleHistoryFilter = (entryType: HistoryEntryType) => {
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      if (next.has(entryType)) {
        if (next.size === 1) return prev;
        next.delete(entryType);
      } else {
        next.add(entryType);
      }
      return next;
    });
  };

  const confirmDeleteSelected = async () => {
    const entriesToDelete = Array.from(selectedEntries.values());
    if (entriesToDelete.length === 0) return;
    setDeleteError(null);
    try {
      await deleteEntries.mutateAsync(entriesToDelete);
      setDeleteConfirmOpen(false);
      setSelectionMode(false);
      setSelectedEntries(new Map());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not delete entries.";
      setDeleteError(message);
    }
  };

  const headerActions = (
    <>
      {selectionMode ? (
        <button
          type="button"
          className="button button--ghost car-detail-action history-action--danger"
          aria-label="Delete selected entries"
          title="Delete selected entries"
          disabled={selectedCount === 0 || deleteEntries.isPending}
          onClick={() => {
            setDeleteError(null);
            setDeleteConfirmOpen(true);
          }}
        >
          <Icon name="trash" />
        </button>
      ) : null}
      <MoreMenu
        triggerIcon="plus"
        triggerAriaLabel="Add history entry"
        triggerClassName="car-detail-action"
        render={() => (
          <>
            <MoreMenuItem label="Add fuel entry" onSelect={openFuelModal} />
            <MoreMenuItem
              label="Add odometer entry"
              onSelect={() => setIsOdometerModalOpen(true)}
            />
          </>
        )}
      />
      <button
        type="button"
        className={
          "button button--ghost car-detail-action" +
          (selectionMode ? " history-action--active" : "")
        }
        aria-label={selectionMode ? "Exit selection mode" : "Select entries"}
        title={selectionMode ? "Exit selection mode" : "Select entries"}
        aria-pressed={selectionMode}
        onClick={toggleSelectionMode}
      >
        <Icon name="checkbox" />
      </button>
      <MoreMenu
        triggerIcon="filter"
        triggerAriaLabel="Filter history"
        triggerClassName="car-detail-action"
        render={() => (
          <>
            {HISTORY_FILTERS.map((item) => (
              <button
                key={item.type}
                type="button"
                role="menuitemcheckbox"
                aria-checked={visibleTypes.has(item.type)}
                className="more-menu__item history-filter__item"
                onClick={() => toggleHistoryFilter(item.type)}
              >
                <span>{item.label}</span>
                {visibleTypes.has(item.type) ? <Icon name="check" /> : null}
              </button>
            ))}
          </>
        )}
      />
    </>
  );

  if (activeCarLoading || isLoading) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading...</p>
      </section>
    );
  }

  if (!hasCars) {
    return (
      <section className="page-section">
        <div className="empty-state">
          <p>You don't have any cars yet.</p>
          <p>Add one in the Garage to start logging fuel.</p>
          <Link to="/garage" className="button button--primary">
            Go to Garage
          </Link>
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="page-section">
        <p className="empty-state">
          Could not load the history: {error?.message ?? "unknown error"}.
        </p>
      </section>
    );
  }

  return (
    <>
      <HistoryList
        entries={entries}
        onAdd={openFuelModal}
        onSelectEntry={setSelectedEntryId}
        headerActions={headerActions}
        selectionMode={selectionMode}
        selectedKeys={new Set(selectedEntries.keys())}
        onToggleSelection={toggleEntrySelection}
      />

      {entries.length > 0 ? (
        <div className="history-pagination" aria-live="polite">
          {isFetchingNextPage ? (
            <span className="history-pagination__status">Loading more...</span>
          ) : canFetchNextPage ? (
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => void fetchNextPage()}
            >
              Load more
            </button>
          ) : (
            <span className="history-pagination__status">End of history</span>
          )}
          <div ref={sentinelRef} className="history-pagination__sentinel" />
        </div>
      ) : null}

      {formOptions.data ? (
        <>
          <NewEntryModal
            isOpen={isFuelModalOpen}
            onClose={closeFuelModal}
            formOptions={formOptions.data}
            cars={cars}
            activeCarId={activeCar?.id ?? null}
            fuelStations={fuelStations.data?.stations ?? []}
            recentFuelStationId={fuelStations.data?.recent_fuel_station_id ?? null}
          />
          <EntryDetailModal
            entryId={selectedEntryId}
            onClose={() => setSelectedEntryId(null)}
            cars={cars}
            formOptions={formOptions.data}
          />
          <OdometerEntryModal
            isOpen={isOdometerModalOpen}
            onClose={() => setIsOdometerModalOpen(false)}
            formOptions={formOptions.data}
            activeCar={activeCar}
          />
        </>
      ) : null}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          if (deleteEntries.isPending) return;
          setDeleteConfirmOpen(false);
          setDeleteError(null);
        }}
        title="Delete entries"
      >
        <p className="confirm-modal__body">Delete {selectedCount} entries?</p>
        {deleteError ? (
          <p className="form-status" data-variant="error" role="alert">
            {deleteError}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="button button--ghost"
            disabled={deleteEntries.isPending}
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteError(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={deleteEntries.isPending || selectedCount === 0}
            onClick={() => void confirmDeleteSelected()}
          >
            {deleteEntries.isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
}
